import { Head, Link, router, usePage } from '@inertiajs/react';

import { trackUmamiEvent } from '@/lib/analytics';
import { PARISH_TIME_ZONE } from '@/lib/datetime';
import type { Event } from '@/pages/Admin/EventEditor';

interface EventsPageProps {
  events: Event[];
  [key: string]: unknown;
}

const dateFormatter = new Intl.DateTimeFormat('pt-PT', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: PARISH_TIME_ZONE,
});

function formatDate(isoString: string | null): string {
  if (!isoString) return '—';
  return dateFormatter.format(new Date(isoString));
}

export default function Events() {
  const { events } = usePage<EventsPageProps>().props;

  function handleDelete(event: Event) {
    if (!window.confirm(`Eliminar o evento "${event.title}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    trackUmamiEvent('admin_content_delete', { content_type: 'event' });
    router.delete(`/events/${event.id}`, { preserveScroll: true });
  }

  return (
    <>
      <Head title="Eventos — Administração" />
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-headline text-on-surface text-2xl font-bold">Eventos</h1>
          <Link
            href="/events/new"
            className="bg-primary hover:bg-primary-dark inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition-colors duration-200"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            Novo evento
          </Link>
        </div>

        <div className="surface-card overflow-hidden">
          {events.length === 0 ? (
            <div className="text-on-surface-light flex flex-col items-center gap-2 py-12 text-center text-sm">
              <span className="material-symbols-outlined text-on-surface-light/40 text-3xl">
                event
              </span>
              Nenhum evento agendado ainda.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] border-collapse text-left">
                <thead>
                  <tr className="border-line border-b">
                    {['Título', 'Local', 'Início', 'Fim'].map((h) => (
                      <th
                        key={h}
                        className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase"
                      >
                        {h}
                      </th>
                    ))}
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr
                      key={event.id}
                      className="border-line hover:bg-background border-b transition-colors last:border-b-0"
                    >
                      <td className="text-on-surface px-4 py-3.5 text-sm font-semibold">
                        <span className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-on-surface-light/50 text-[16px]">
                            event
                          </span>
                          {event.title}
                        </span>
                      </td>
                      <td className="text-on-surface-light px-4 py-3.5 text-sm">
                        {event.location ?? '—'}
                      </td>
                      <td className="text-on-surface-light px-4 py-3.5 text-xs">
                        {formatDate(event.startsAt)}
                      </td>
                      <td className="text-on-surface-light px-4 py-3.5 text-xs">
                        {formatDate(event.endsAt)}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <Link
                            href={`/events/${event.id}/edit`}
                            aria-label="Editar evento"
                            className="text-on-surface-light hover:bg-primary/10 hover:text-primary rounded-md p-1.5 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[18px]">edit</span>
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDelete(event)}
                            aria-label="Eliminar evento"
                            className="text-on-surface-light hover:bg-error/10 hover:text-error rounded-md p-1.5 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
